import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearOrgUsersSearch,
  fetchOrgUsersListRequest,
  provideOrgAccessRequest,
  removeOrgUserAccessRequest,
  searchOrgUsersRequest,
} from "../../../containers/AdminPanel/features/orgAccess/orgAccessSlice";
import useLiveRefresh from "@utils/useLiveRefresh";

const useOrganizationAccess = () => {
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showRemoveUserModal, setShowRemoveUserModal] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [nameSortAsc, setNameSortAsc] = useState(null);

  const { orgUsersList, orgUserSearchedResult, listLoaded, searchLoading } =
    useSelector((state) => state.adminPanel.orgUserData);

  useEffect(() => {
    dispatch(fetchOrgUsersListRequest());
  }, [dispatch]);

  // Community access changes arrive as organization.community.*.
  useLiveRefresh(["organization"], () => {
    dispatch(fetchOrgUsersListRequest());
  });

  const filteredOrgUsersList = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    const list = term
      ? orgUsersList.filter((user) =>
          (user.email || "").toLowerCase().includes(term),
        )
      : [...orgUsersList];
    if (nameSortAsc === null) return list;
    return list.sort((a, b) => {
      const an = (a.firstName || "").toLowerCase();
      const bn = (b.firstName || "").toLowerCase();
      if (an < bn) return nameSortAsc ? -1 : 1;
      if (an > bn) return nameSortAsc ? 1 : -1;
      return 0;
    });
  }, [orgUsersList, searchText, nameSortAsc]);

  const toggleSortByName = () => {
    setNameSortAsc((prev) => (prev === null ? true : !prev));
  };

  const handleAddUserClick = () => {
    // No user list call on open — the modal searches once an email is entered.
    dispatch(clearOrgUsersSearch());
    setShowAddUserModal(true);
  };

  const closeAddUserModal = () => {
    setShowAddUserModal(false);
    dispatch(clearOrgUsersSearch());
  };

  const searchAddableUsers = (email) => {
    if (!email) {
      dispatch(clearOrgUsersSearch());
      return;
    }
    dispatch(searchOrgUsersRequest({ email }));
  };

  const handleRemoveUserClick = (communityId, userId) => {
    setUserToRemove({ communityId, userId });
    setShowRemoveUserModal(true);
  };

  const provideOrgAccess = (userId) => {
    if (!userId) return;
    dispatch(provideOrgAccessRequest({ userId }));
    closeAddUserModal();
  };

  const removeOrgUserAccess = () => {
    if (!userToRemove?.communityId) return;
    const { communityId, userId } = userToRemove;
    dispatch(removeOrgUserAccessRequest({ communityId, userId }));
    setShowRemoveUserModal(false);
    setUserToRemove(null);
  };

  return {
    hasOrgUsers: orgUsersList.length > 0,
    listLoaded,
    handleAddUserClick,
    handleRemoveUserClick,
    showAddUserModal,
    closeAddUserModal,
    searchAddableUsers,
    searchLoading,
    handleSearchUserOnChange: setSearchText,
    filteredOrgUsersList,
    toggleSortByName,
    provideOrgAccess,
    removeOrgUserAccess,
    searchText,
    showRemoveUserModal,
    setShowRemoveUserModal,
    searchedUsers: orgUserSearchedResult,
  };
};

export default useOrganizationAccess;
